import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Notifications dropdown — tenant-aware + clickable.
 *
 * Demo mode → curated list of recent events for the active tenant; each
 * item deep-links to the relevant page (incident register, agent detail,
 * report library, etc.) so the demo can drill straight in.
 *
 * Customer mode → empty state.
 */
export default function NotificationsMenu({
  open,
  onToggle,
  isDemo,
  showDot,
  tenantId,
}) {
  const navigate = useNavigate();
  const items = useMemo(() => (isDemo ? notificationsFor(tenantId) : []), [isDemo, tenantId]);

  const go = (path) => {
    onToggle();
    navigate(path);
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={onToggle}
        className="relative h-9 w-9 inline-flex items-center justify-center rounded-md hover:bg-inset transition-colors"
        aria-label="Notifications"
        data-testid="header-notifications-trigger"
      >
        <Bell size={18} className="text-ink-secondary" />
        {showDot && (
          <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-risk-high ring-2 ring-raised" />
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-[380px] rounded-md border border-border-subtle bg-raised shadow-e3 anim-fade-up overflow-hidden z-50"
          data-testid="header-notifications-menu"
        >
          <div className="px-3 py-2 border-b border-border-subtle flex items-center justify-between">
            <p className="eyebrow">Notifications</p>
            <span className="text-meta text-ink-secondary">
              {items.length} unread
            </span>
          </div>
          <ul className="max-h-[420px] overflow-y-auto py-1">
            {items.length === 0 ? (
              <EmptyState />
            ) : (
              items.map((n) => (
                <NotificationItem key={n.id} item={n} onOpen={() => go(n.path)} />
              ))
            )}
          </ul>
          {items.length > 0 && (
            <div className="px-3 py-2 border-t border-border-subtle text-meta text-ink-secondary text-center">
              Updates roll up to the Audit Log · click any row to drill in.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <li className="px-4 py-8 text-center text-meta text-ink-secondary">
      No notifications yet. They will appear here once your tenant is active.
    </li>
  );
}

const sevDot = {
  high: "bg-risk-high",
  med: "bg-gold-600",
  low: "bg-tralenex-600",
  ok: "bg-risk-low",
};

function NotificationItem({ item, onOpen }) {
  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        className="w-full text-left px-3 py-2.5 hover:bg-inset cursor-pointer transition-colors border-b border-border-subtle/60 last:border-b-0"
        data-testid={`notification-${item.id}`}
      >
        <div className="flex items-start gap-2.5">
          <span className={cn("mt-1.5 h-1.5 w-1.5 rounded-full shrink-0", sevDot[item.severity])} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-ink-primary">{item.title}</p>
            <p className="text-meta text-ink-secondary mt-0.5 line-clamp-2">{item.body}</p>
          </div>
          <div className="text-right shrink-0 flex flex-col items-end gap-1">
            <span className="text-meta text-ink-disabled tabular-nums">{item.ts}</span>
            <ChevronRight size={12} className="text-ink-disabled" />
          </div>
        </div>
      </button>
    </li>
  );
}

// ── Tenant-aware seeded notifications ─────────────────────────────
function notificationsFor(tenantId) {
  if (tenantId === "northwind-insurance") {
    return [
      { id: "n-ins-1", severity: "high", title: "Approval needed · Claims Triage Assistant",
        body: "Stage 4 — CAIO review. SLA 01d 18h.", ts: "13:21", path: "/deploy/requests" },
      { id: "n-ins-2", severity: "med", title: "Drift alert · Claims Fraud Indicator Agent",
        body: "Hallucination rate ↑ 0.08% on 3,120-call sample.", ts: "11:42", path: "/measure/performance" },
      { id: "n-ins-3", severity: "low", title: "Evidence pack delivered · APRA CPS 234",
        body: "Quarterly Evidence signed by Bianca Romano.", ts: "09:14", path: "/reports/library" },
      { id: "n-ins-4", severity: "high", title: "Incident raised · INC-INS-006",
        body: "Customer-facing chatbot returned a misquote — investigation open.", ts: "08:58", path: "/govern/incidents" },
      { id: "n-ins-5", severity: "ok", title: "Vendor health · Vertex AI back to healthy",
        body: "Latency normalised after p95 spike at 03:12.", ts: "06:02", path: "/vendors/performance" },
    ];
  }
  // Default → Acme Bank
  return [
    { id: "n-bank-1", severity: "high", title: "Approval needed · Mortgage Underwriting Agent",
      body: "Stage 4 — CAIO review. SLA 02d 04h.", ts: "14:02", path: "/deploy/requests" },
    { id: "n-bank-2", severity: "med", title: "Drift alert · Fraud Triage Copilot",
      body: "Hallucination rate ↑ 0.10% (sample of 4,210 calls).", ts: "11:48", path: "/measure/performance" },
    { id: "n-bank-3", severity: "low", title: "Evidence pack delivered · APRA CPS 234",
      body: "Signed and shared with Internal Audit.", ts: "09:01", path: "/reports/library" },
    { id: "n-bank-4", severity: "high", title: "Policy updated · POL-BANK-103",
      body: "Customer-PII redaction rule v2 published by Priya Ravi.", ts: "08:23", path: "/govern/policies" },
    { id: "n-bank-5", severity: "ok", title: "Audit chain verified",
      body: "Daily hash-chain integrity check passed.", ts: "06:00", path: "/settings/audit" },
  ];
}
